import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from 'sonner';
import { 
  LayoutDashboard, GitBranch, MessageSquare, User, LogOut, 
  Plus, Send, Phone, Menu, X, ChevronDown, Globe,
  CreditCard, Users, TrendingUp, Award, DollarSign,
  Settings, Search, Edit, Mail, FileText, Share2, CheckCircle, AlertCircle,
  Trash2, ToggleLeft, ToggleRight, MessageCircle,
  Bell, Download, BarChart3, Sun, Moon, Filter, ArrowUpRight, Clock,
  ShoppingBag, BookOpen, Briefcase
} from 'lucide-react';
import { BoutiquePage } from './components/v2/BoutiquePage';
import { EducationPage } from './components/v2/EducationPage';
import { BusinessHubPage } from './components/v2/BusinessHubPage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
// Always use relative API path so it works on any domain (preview + production)
const API = '/api';

axios.defaults.withCredentials = true;

// Translations
const translations = {
  en: {
    dashboard: "Dashboard",
    genealogy: "Genealogy",
    chat: "Chat",
    profile: "Profile",
    admin: "Admin",
    whatsappConfig: "WhatsApp",
    login: "Login",
    register: "Register",
    logout: "Logout",
    email: "Email",
    password: "Password",
    name: "Full Name",
    phone: "Phone",
    phoneNumber: "Phone Number",
    sponsorId: "Sponsor ID (MAGAZEN-XXXXX)",
    sponsorIdRequired: "Sponsor ID is required",
    invalidSponsor: "Invalid Sponsor ID",
    validSponsor: "Valid sponsor",
    welcome: "Welcome to MAGAZEN",
    subtitle: "Build your 6x4 matrix and achieve leadership",
    totalMembers: "Total Members",
    totalPW: "Total PW",
    currentRank: "Current Rank",
    totalEarnings: "Earnings (HTG)",
    directRecruits: "Direct Recruits",
    selectMatrix: "Select Matrix",
    createMatrix: "Create Matrix",
    addMember: "Add Member",
    paySubscription: "Pay Subscription (14,000 HTG)",
    shareWhatsApp: "Share on WhatsApp",
    weeklyReport: "Weekly Report",
    sendEmail: "Send Email",
    needHelp: "Need Help?",
    whatsappAccounts: "WhatsApp Accounts",
    addAccount: "Add Account",
    description: "Description",
    setDefault: "Set as Default",
    default: "Default",
    active: "Active",
    inactive: "Inactive",
    level: "Level",
    emptySlot: "Empty",
    noRank: "Not Ranked",
    monthlyFee: "Monthly Subscription: 14,000 HTG",
    getStarted: "Get Started",
    alreadyHaveAccount: "Already have an account?",
    dontHaveAccount: "Don't have an account?",
    or: "or",
    continueWithGoogle: "Continue with Google",
    sending: "Sending...",
    typeMessage: "Type a message...",
    conversations: "Conversations",
    noConversations: "No conversations yet",
    referralLink: "Referral Link",
    copied: "Copied!",
    paymentSuccess: "Payment Successful!",
    paymentCancel: "Payment Cancelled",
    loading: "Loading...",
    users: "Users",
    searchUsers: "Search users...",
    editUser: "Edit User",
    save: "Save",
    cancel: "Cancel",
    sponsor: "Sponsor",
    transactions: "Transactions",
    transactionHistory: "Transaction History",
    analytics: "Analytics",
    teamAnalytics: "Team Analytics",
    exportCSV: "Export CSV",
    weeklyGrowth: "Weekly Growth",
    levelDistribution: "Level Distribution",
    rankMaintenance: "Rank Maintenance",
    noTransactions: "No transactions yet",
    amount: "Amount",
    status: "Status",
    date: "Date",
    pending: "Pending",
    completed: "Completed",
    failed: "Failed",
    notifications: "Notifications",
    noNotifications: "No notifications",
    markAllRead: "Mark all read",
    newMembers: "New Members",
    theme: "Theme",
    darkMode: "Dark",
    lightMode: "Light",
    searchGenealogy: "Search genealogy...",
    downloadReport: "Download Report",
    consecutiveMonths: "Consecutive Months",
    bonusTarget: "Bonus Target",
    installApp: "Install App",
    forgotPassword: "Forgot password?",
    resetPassword: "Reset Password",
    newPassword: "New Password",
    confirmPassword: "Confirm Password",
    sendResetLink: "Send Reset Link",
    resetLinkSent: "Reset link sent! Check your email.",
    passwordReset: "Password reset successfully!",
    backToLogin: "Back to Login",
    enterEmail: "Enter your email to receive a reset link",
    optional: "Optional"
  },
  fr: {
    dashboard: "Tableau de bord",
    genealogy: "Généalogie",
    chat: "Discussion",
    profile: "Profil",
    admin: "Admin",
    whatsappConfig: "WhatsApp",
    login: "Connexion",
    register: "S'inscrire",
    logout: "Déconnexion",
    email: "E-mail",
    password: "Mot de passe",
    name: "Nom complet",
    phone: "Téléphone",
    phoneNumber: "Numéro de téléphone",
    sponsorId: "ID Parrain (MAGAZEN-XXXXX)",
    sponsorIdRequired: "ID Parrain requis",
    invalidSponsor: "ID Parrain invalide",
    validSponsor: "Parrain valide",
    welcome: "Bienvenue à MAGAZEN",
    subtitle: "Construisez votre matrice 6x4 et atteignez le leadership",
    totalMembers: "Membres Totaux",
    totalPW: "PW Totaux",
    currentRank: "Rang Actuel",
    totalEarnings: "Revenus (HTG)",
    directRecruits: "Recrutement Direct",
    selectMatrix: "Sélectionner Matrice",
    createMatrix: "Créer Matrice",
    addMember: "Ajouter Membre",
    paySubscription: "Payer Abonnement (14,000 HTG)",
    shareWhatsApp: "Partager sur WhatsApp",
    weeklyReport: "Rapport Hebdomadaire",
    sendEmail: "Envoyer Email",
    level: "Niveau",
    emptySlot: "Vide",
    noRank: "Non classé",
    monthlyFee: "Abonnement mensuel: 14,000 HTG",
    getStarted: "Commencer",
    alreadyHaveAccount: "Vous avez déjà un compte?",
    dontHaveAccount: "Vous n'avez pas de compte?",
    or: "ou",
    continueWithGoogle: "Continuer avec Google",
    sending: "Envoi...",
    typeMessage: "Tapez un message...",
    conversations: "Conversations",
    noConversations: "Aucune conversation",
    referralLink: "Lien de parrainage",
    copied: "Copié!",
    paymentSuccess: "Paiement réussi!",
    paymentCancel: "Paiement annulé",
    loading: "Chargement...",
    users: "Utilisateurs",
    searchUsers: "Rechercher...",
    editUser: "Modifier Utilisateur",
    save: "Sauvegarder",
    cancel: "Annuler",
    sponsor: "Parrain",
    needHelp: "Besoin d'aide?",
    whatsappAccounts: "Comptes WhatsApp",
    addAccount: "Ajouter Compte",
    description: "Description",
    setDefault: "Définir par défaut",
    default: "Par défaut",
    active: "Actif",
    inactive: "Inactif",
    transactions: "Transactions",
    transactionHistory: "Historique des transactions",
    analytics: "Analytique",
    teamAnalytics: "Analytique d'équipe",
    exportCSV: "Exporter CSV",
    weeklyGrowth: "Croissance hebdomadaire",
    levelDistribution: "Distribution par niveau",
    rankMaintenance: "Maintien du rang",
    noTransactions: "Aucune transaction",
    amount: "Montant",
    status: "Statut",
    date: "Date",
    pending: "En attente",
    completed: "Complété",
    failed: "Échoué",
    notifications: "Notifications",
    noNotifications: "Aucune notification",
    markAllRead: "Tout marquer comme lu",
    newMembers: "Nouveaux membres",
    theme: "Thème",
    darkMode: "Sombre",
    lightMode: "Clair",
    searchGenealogy: "Rechercher dans la généalogie...",
    downloadReport: "Télécharger le rapport",
    consecutiveMonths: "Mois consécutifs",
    bonusTarget: "Objectif bonus",
    installApp: "Installer l'app",
    forgotPassword: "Mot de passe oublié?",
    resetPassword: "Réinitialiser le mot de passe",
    newPassword: "Nouveau mot de passe",
    confirmPassword: "Confirmer le mot de passe",
    sendResetLink: "Envoyer le lien",
    resetLinkSent: "Lien envoyé! Vérifiez votre email.",
    passwordReset: "Mot de passe réinitialisé!",
    backToLogin: "Retour à la connexion",
    enterEmail: "Entrez votre email pour recevoir un lien de réinitialisation",
    optional: "Optionnel"
  },
  ht: {
    dashboard: "Tablo Bò",
    genealogy: "Jeneraloji",
    chat: "Tchate",
    profile: "Pwofil",
    admin: "Admin",
    whatsappConfig: "WhatsApp",
    login: "Konekte",
    register: "Enskri",
    logout: "Dekonekte",
    email: "Imèl",
    password: "Modpas",
    name: "Non Konplè",
    phone: "Telefòn",
    phoneNumber: "Nimewo Telefòn",
    sponsorId: "ID Parenn (MAGAZEN-XXXXX)",
    sponsorIdRequired: "ID Parenn obligatwa",
    invalidSponsor: "ID Parenn pa valid",
    validSponsor: "Parenn valid",
    welcome: "Byenveni nan MAGAZEN",
    subtitle: "Bati matris 6x4 ou epi rive nan lidèship",
    totalMembers: "Total Manm",
    totalPW: "Total PW",
    currentRank: "Nivo Kounye a",
    totalEarnings: "Revni (HTG)",
    directRecruits: "Rekritman Dirèk",
    selectMatrix: "Chwazi Matris",
    createMatrix: "Kreye Matris",
    addMember: "Ajoute Manm",
    paySubscription: "Peye Abònman (14,000 HTG)",
    shareWhatsApp: "Pataje sou WhatsApp",
    weeklyReport: "Rapò Semen",
    sendEmail: "Voye Imèl",
    level: "Nivo",
    emptySlot: "Vid",
    noRank: "Pa ranke",
    monthlyFee: "Abònman chak mwa: 14,000 HTG",
    getStarted: "Kòmanse",
    alreadyHaveAccount: "Ou gen yon kont deja?",
    dontHaveAccount: "Ou pa gen yon kont?",
    or: "oswa",
    continueWithGoogle: "Kontinye ak Google",
    sending: "Ap voye...",
    typeMessage: "Tape yon mesaj...",
    conversations: "Konvèsasyon",
    noConversations: "Pa gen konvèsasyon ankò",
    referralLink: "Lyen Parenaj",
    copied: "Kopye!",
    paymentSuccess: "Peman reyisi!",
    paymentCancel: "Peman anile",
    loading: "Ap chaje...",
    users: "Itilizatè yo",
    searchUsers: "Chèche itilizatè...",
    editUser: "Modifye Itilizatè",
    save: "Sove",
    cancel: "Anile",
    sponsor: "Parenn",
    needHelp: "Bezwen Èd?",
    whatsappAccounts: "Kont WhatsApp yo",
    addAccount: "Ajoute Kont",
    description: "Deskripsyon",
    setDefault: "Mete pa defo",
    default: "Pa defo",
    active: "Aktif",
    inactive: "Inaktif",
    transactions: "Tranzaksyon",
    transactionHistory: "Istorik Tranzaksyon",
    analytics: "Analitik",
    teamAnalytics: "Analitik Ekip",
    exportCSV: "Telechaje CSV",
    weeklyGrowth: "Kwasans Semèn",
    levelDistribution: "Distribisyon pa Nivo",
    rankMaintenance: "Mantni Rang",
    noTransactions: "Pa gen tranzaksyon ankò",
    amount: "Montan",
    status: "Estati",
    date: "Dat",
    pending: "Ap tann",
    completed: "Fini",
    failed: "Echwe",
    notifications: "Notifikasyon",
    noNotifications: "Pa gen notifikasyon",
    markAllRead: "Make tout li",
    newMembers: "Nouvo Manm",
    theme: "Tèm",
    darkMode: "Fonse",
    lightMode: "Klere",
    searchGenealogy: "Chèche nan jeneraloji...",
    downloadReport: "Telechaje Rapò",
    consecutiveMonths: "Mwa Konsekitif",
    bonusTarget: "Objektif Bonis",
    installApp: "Enstale App",
    forgotPassword: "Bliye modpas?",
    resetPassword: "Reset Modpas",
    newPassword: "Nouvo Modpas",
    confirmPassword: "Konfime Modpas",
    sendResetLink: "Voye lyen reset",
    resetLinkSent: "Lyen voye! Tcheke imèl ou.",
    passwordReset: "Modpas reset avèk siksè!",
    backToLogin: "Retounen nan koneksyon",
    enterEmail: "Mete imèl ou pou resevwa yon lyen reset",
    optional: "Opsyonèl"
  }
};

// Context
const AuthContext = createContext(null);
const LanguageContext = createContext(null);

// Auth Provider
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    setUser(response.data);
    return response.data;
  };

  const register = async (data) => {
    const response = await axios.post(`${API}/auth/register`, data);
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Language Provider
export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => localStorage.getItem('lang') || 'ht');
  
  const t = useCallback((key) => translations[language]?.[key] || translations.en[key] || key, [language]);
  
  const changeLanguage = (lang) => {
    setLanguage(lang);
    localStorage.setItem('lang', lang);
  };

  return (
    <LanguageContext.Provider value={{ language, t, changeLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

const useAuth = () => useContext(AuthContext);
const useLanguage = () => useContext(LanguageContext);

// Language Selector
const LanguageSelector = () => {
  const { language, changeLanguage } = useLanguage();
  const [open, setOpen] = useState(false);
  
  const languages = [
    { code: 'ht', name: 'Kreyòl' },
    { code: 'fr', name: 'Français' },
    { code: 'en', name: 'English' }
  ];
  
  return (
    <div className="relative">
      <button 
        data-testid="language-selector"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/10 transition-colors text-white"
      >
        <Globe className="w-4 h-4" />
        <span className="text-sm font-medium">{languages.find(l => l.code === language)?.name}</span>
        <ChevronDown className="w-4 h-4" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-[#1a1a25] rounded-lg shadow-lg border border-[#2d2d3a] py-1 z-50">
          {languages.map(lang => (
            <button
              key={lang.code}
              data-testid={`lang-${lang.code}`}
              onClick={() => { changeLanguage(lang.code); setOpen(false); }}
              className={`w-full px-4 py-2 text-left text-sm hover:bg-white/10 ${language === lang.code ? 'text-red-400 font-semibold' : 'text-white'}`}
            >
              {lang.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-red-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  return children;
};

// Admin Route
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f]">
        <div className="w-12 h-12 border-4 border-red-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (!user || user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Auth Callback (OAuth)
const AuthCallback = () => {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const [needsSponsor, setNeedsSponsor] = useState(false);
  const [sponsorId, setSponsorId] = useState('');
  const [sessionId, setSessionId] = useState('');
  
  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    
    const hash = window.location.hash;
    const sid = hash.split('session_id=')[1]?.split('&')[0];
    
    if (sid) {
      setSessionId(sid);
      // Check if user exists
      processSession(sid, null);
    } else {
      navigate('/login', { replace: true });
    }
  }, [navigate]);
  
  const processSession = async (sid, sponsor) => {
    try {
      const response = await axios.post(`${API}/auth/session`, { 
        session_id: sid,
        sponsor_id: sponsor
      });
      setUser(response.data);
      navigate('/dashboard', { replace: true });
    } catch (error) {
      if (error.response?.data?.detail?.includes('Sponsor ID required')) {
        setNeedsSponsor(true);
      } else {
        console.error('OAuth error:', error);
        navigate('/login', { replace: true });
      }
    }
  };
  
  const handleSponsorSubmit = (e) => {
    e.preventDefault();
    if (sponsorId.trim()) {
      processSession(sessionId, sponsorId.trim());
    }
  };
  
  if (needsSponsor) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center p-4">
        <div className="card-dark w-full max-w-md">
          <h2 className="text-xl font-bold text-white mb-4">Enter Sponsor ID</h2>
          <p className="text-gray-400 mb-6">Please enter your sponsor's MAGAZEN ID to complete registration.</p>
          <form onSubmit={handleSponsorSubmit} className="space-y-4">
            <input
              type="text"
              value={sponsorId}
              onChange={(e) => setSponsorId(e.target.value)}
              placeholder="MAGAZEN-XXXXX"
              className="input-dark"
              required
            />
            <button type="submit" className="btn-primary w-full">Continue</button>
          </form>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f]">
      <div className="w-12 h-12 border-4 border-red-500 border-t-transparent rounded-full animate-spin"></div>
    </div>
  );
};

// Landing Page
const LandingPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      <header className="sticky top-0 z-50 glass-dark">
        <div className="max-w-7xl mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img src="/logo.jpg" alt="LeadMotiv Hayti" className="h-12 w-auto" />
            <span className="text-yellow-400 font-bold text-xl hidden sm:inline">MAGAZEN LETA</span>
          </div>
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/boutique" className="text-gray-400 hover:text-white text-sm font-medium transition-colors">Boutique</Link>
            <Link to="/docs/opportunity.html" className="text-gray-400 hover:text-white text-sm font-medium transition-colors">Opòtinite</Link>
            <Link to="/education" className="text-gray-400 hover:text-white text-sm font-medium transition-colors">Edikasyon</Link>
          </nav>
          <div className="flex items-center gap-3">
            <LanguageSelector />
            <button 
              data-testid="login-btn"
              onClick={() => navigate('/login')}
              className="bg-red-500 text-white px-4 py-2 rounded-lg font-bold hover:bg-red-600 transition-colors"
            >
              {t('login')}
            </button>
          </div>
        </div>
      </header>
      
      <main className="max-w-6xl mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-red-500 font-bold mb-2">LEAD MOTIV H HAYTI</p>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight mb-4">
              <span className="text-yellow-400">BYENVINI NAN MAGAZEN</span><br />
              <span className="text-white">LETA ANPI HAYTI A.</span>
            </h2>
            <p className="text-lg text-gray-400 mb-8">
              An nou dekouvri pwodwi ekstraèdinè ke Endijèn HAYTYEN fè pandan y'ap bati gwo kominote biznis.
            </p>
            
            <div className="flex flex-wrap gap-4">
              <button
                data-testid="get-started-btn"
                onClick={() => navigate('/register')}
                className="btn-gold text-lg px-8 py-4"
              >
                AN NOU VANSE ANSANM
              </button>
            </div>
          </div>
          
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-red-500/20 to-yellow-500/20 rounded-3xl blur-3xl"></div>
            <div className="relative bg-[#12121a] rounded-3xl p-8 border border-[#2d2d3a]">
              <div className="text-center mb-8">
                <img 
                  src="/logo.jpg" 
                  alt="LeadMotiv Hayti" 
                  className="w-64 h-auto mx-auto mb-4"
                />
                <p className="text-gray-400 italic mt-2">Le Magnat du Divin!</p>
              </div>
              
              <div className="bg-[#1a1a25] rounded-xl p-6 mb-6">
                <div className="flex items-center justify-center gap-3 text-yellow-400 mb-2">
                  <DollarSign className="w-8 h-8" />
                  <span className="text-3xl font-bold">14,000 HTG</span>
                </div>
                <p className="text-gray-400 text-center">{t('monthlyFee')}</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Rank Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-16">
          {[
            { level: 1, name: 'Nivo 1', title: 'Lidè kap vini', bonus: '25,000 HTG', members: 6, color: 'green' },
            { level: 2, name: 'Nivo 2', title: 'Lidè konfime', bonus: '200,000 HTG', members: 42, color: 'blue' },
            { level: 3, name: 'Nivo 3', title: 'Lidè eksperyans', bonus: '300,000 HTG', members: 258, color: 'purple' },
            { level: 4, name: 'Nivo LO', title: 'NIVO LO FONSE', bonus: '1.5M HTG', members: 1554, color: 'yellow' },
          ].map((rank) => (
            <div key={rank.level} className={`card-dark border-${rank.color}-500/30 hover:border-${rank.color}-500 transition-colors`}>
              <div className={`rank-badge nivo-${rank.level} mb-3`}>{rank.name}</div>
              <div className="text-xl font-bold text-white">{rank.bonus}</div>
              <div className="text-sm text-gray-400">{rank.members} manm</div>
              <div className="text-xs text-gray-500 mt-2">{rank.title}</div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
};

// Login Page
const LoginPage = () => {
  const { t } = useLanguage();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Login failed');
    } finally {
      setLoading(false);
    }
  };
  
  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  
  return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/logo.jpg" alt="LeadMotiv Hayti" className="w-32 h-auto mx-auto mb-4" />
          <h1 className="text-2xl font-extrabold text-yellow-400 mb-2">
            MAGAZEN LETA ANPI HAYTI A
          </h1>
          <p className="text-gray-400">{t('login')}</p>
        </div>
        
        <div className="card-dark">
          {error && (
            <div className="bg-red-900/50 border border-red-500 text-red-300 px-4 py-3 rounded-lg mb-4 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">{t('email')}</label>
              <input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-dark"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">{t('password')}</label>
              <input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-dark"
                required
              />
            </div>
            
            <button
              data-testid="login-submit"
              type="submit"
              disabled={loading}
              className="btn-primary w-full py-3"
            >
              {loading ? t('loading') : t('login')}
            </button>

            <div className="text-right mt-2">
              <Link to="/forgot-password" data-testid="forgot-password-link" className="text-sm text-gray-400 hover:text-red-400 transition-colors">
                {t('forgotPassword')}
              </Link>
            </div>
          </form>
          
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[#2d2d3a]"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-[#12121a] text-gray-500">{t('or')}</span>
            </div>
          </div>
          
          <button
            data-testid="google-login"
            onClick={handleGoogleLogin}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white text-black rounded-lg font-semibold hover:bg-gray-100 transition-colors"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span>{t('continueWithGoogle')}</span>
          </button>
          
          <p className="text-center text-sm text-gray-500 mt-6">
            {t('dontHaveAccount')}{' '}
            <Link to="/register" className="text-red-400 font-semibold hover:underline">
              {t('register')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

// Forgot Password Page
const ForgotPasswordPage = () => {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (error) {
      // Even on network error, show success to prevent email enumeration
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/logo.jpg" alt="LeadMotiv Hayti" className="w-32 h-auto mx-auto mb-4" />
          <h1 className="text-2xl font-extrabold text-yellow-400 mb-2">{t('forgotPassword')}</h1>
          <p className="text-gray-400 text-sm">{t('enterEmail')}</p>
        </div>

        <div className="card-dark">
          {sent ? (
            <div className="text-center py-6">
              <Mail className="w-16 h-16 text-green-400 mx-auto mb-4" />
              <p className="text-white mb-2">{t('resetLinkSent')}</p>
              <p className="text-gray-400 text-sm mb-6">{email}</p>
              <Link to="/login" className="btn-primary inline-block px-6 py-2">{t('backToLogin')}</Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">{t('email')}</label>
                <input
                  data-testid="forgot-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email@example.com"
                  className="input-dark"
                  required
                />
              </div>
              <button
                data-testid="forgot-submit"
                type="submit"
                disabled={loading}
                className="btn-primary w-full py-3"
              >
                {loading ? t('loading') : t('sendResetLink')}
              </button>
            </form>
          )}

          <p className="text-center text-sm text-gray-500 mt-6">
            <Link to="/login" className="text-red-400 font-semibold hover:underline">
              {t('backToLogin')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

// Reset Password Page
const ResetPasswordPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [password, setPassword] = useState('');
  const [confirmPass, setConfirmPass] = useState('');
  const [loading, setLoading] = useState(false);

  const token = new URLSearchParams(location.search).get('token');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPass) {
      toast.error('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      toast.success(t('passwordReset'));
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reset failed');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center p-4">
        <div className="card-dark text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <p className="text-white mb-4">Invalid reset link</p>
          <Link to="/login" className="btn-primary inline-block px-6 py-2">{t('backToLogin')}</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/logo.jpg" alt="LeadMotiv Hayti" className="w-32 h-auto mx-auto mb-4" />
          <h1 className="text-2xl font-extrabold text-yellow-400 mb-2">{t('resetPassword')}</h1>
        </div>

        <div className="card-dark">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">{t('newPassword')}</label>
              <input
                data-testid="reset-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-dark"
                required
                minLength={6}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">{t('confirmPassword')}</label>
              <input
                data-testid="reset-confirm"
                type="password"
                value={confirmPass}
                onChange={(e) => setConfirmPass(e.target.value)}
                className="input-dark"
                required
                minLength={6}
              />
            </div>
            <button
              data-testid="reset-submit"
              type="submit"
              disabled={loading}
              className="btn-primary w-full py-3"
            >
              {loading ? t('loading') : t('resetPassword')}
            </button>
          </form>

          <p className="text-center text-sm text-gray-500 mt-6">
            <Link to="/login" className="text-red-400 font-semibold hover:underline">
              {t('backToLogin')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

// Register Page with mandatory Sponsor ID
const RegisterPage = () => {
  const { t } = useLanguage();
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', password: '', sponsor_id: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sponsorValid, setSponsorValid] = useState(null);
  const [sponsorName, setSponsorName] = useState('');
  const [checkingSponsor, setCheckingSponsor] = useState(false);
  
  // Get sponsor from URL params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get('ref');
    if (ref) {
      const sponsorId = ref.split('_')[0];
      setFormData(prev => ({ ...prev, sponsor_id: sponsorId }));
      validateSponsor(sponsorId);
    }
  }, [location]);
  
  const validateSponsor = async (sponsorId) => {
    if (!sponsorId || sponsorId.length < 5) {
      setSponsorValid(null);
      setSponsorName('');
      return;
    }
    
    setCheckingSponsor(true);
    try {
      const response = await axios.get(`${API}/auth/validate-sponsor/${sponsorId}`);
      setSponsorValid(true);
      setSponsorName(response.data.sponsor_name);
    } catch (err) {
      setSponsorValid(false);
      setSponsorName('');
    } finally {
      setCheckingSponsor(false);
    }
  };
  
  const handleSponsorChange = (e) => {
    const value = e.target.value.toUpperCase();
    setFormData({ ...formData, sponsor_id: value });
    if (value.length >= 10) {
      validateSponsor(value);
    } else {
      setSponsorValid(null);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Only validate sponsor if provided
    if (formData.sponsor_id && !sponsorValid) {
      setError(t('invalidSponsor'));
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      await register(formData);
      navigate('/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Registration failed');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/logo.jpg" alt="LeadMotiv Hayti" className="w-32 h-auto mx-auto mb-4" />
          <h1 className="text-2xl font-extrabold text-yellow-400 mb-2">
            MAGAZEN LETA ANPI HAYTI A
          </h1>
          <p className="text-gray-400">{t('register')}</p>
        </div>
        
        <div className="card-dark">
          {error && (
            <div className="bg-red-900/50 border border-red-500 text-red-300 px-4 py-3 rounded-lg mb-4 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Sponsor ID - OPTIONAL */}
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">
                {t('sponsorId')} <span className="text-gray-600 text-xs">({t('optional') || 'Optional'})</span>
              </label>
              <div className="relative">
                <input
                  data-testid="register-sponsor"
                  type="text"
                  value={formData.sponsor_id}
                  onChange={handleSponsorChange}
                  placeholder="MAGAZEN-XXXXX"
                  className={`input-dark pr-10 ${
                    sponsorValid === true ? 'border-green-500' : 
                    sponsorValid === false ? 'border-red-500' : ''
                  }`}
                  required
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {checkingSponsor && <div className="w-5 h-5 border-2 border-gray-500 border-t-transparent rounded-full animate-spin"></div>}
                  {!checkingSponsor && sponsorValid === true && <CheckCircle className="w-5 h-5 text-green-500" />}
                  {!checkingSponsor && sponsorValid === false && <AlertCircle className="w-5 h-5 text-red-500" />}
                </div>
              </div>
              {sponsorValid === true && (
                <p className="text-green-400 text-sm mt-1 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" />
                  {t('validSponsor')}: {sponsorName}
                </p>
              )}
              {sponsorValid === false && (
                <p className="text-red-400 text-sm mt-1">{t('invalidSponsor')}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">{t('name')}</label>
              <input
                data-testid="register-name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="input-dark"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">{t('email')}</label>
              <input
                data-testid="register-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="input-dark"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">{t('phone')}</label>
              <input
                data-testid="register-phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="input-dark"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">{t('password')}</label>
              <input
                data-testid="register-password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="input-dark"
                required
                minLength={6}
              />
            </div>
            
            <button
              data-testid="register-submit"
              type="submit"
              disabled={loading || !sponsorValid}
              className="btn-primary w-full py-3 disabled:opacity-50"
            >
              {loading ? t('loading') : t('register')}
            </button>
          </form>
          
          <p className="text-center text-sm text-gray-500 mt-6">
            {t('alreadyHaveAccount')}{' '}
            <Link to="/login" className="text-red-400 font-semibold hover:underline">
              {t('login')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

// Dashboard Layout
const DashboardLayout = ({ children }) => {
  const { t } = useLanguage();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: t('dashboard') },
    { path: '/business', icon: Briefcase, label: 'Centre Affaires' },
    { path: '/genealogy', icon: GitBranch, label: t('genealogy') },
    { path: '/analytics', icon: BarChart3, label: t('analytics') },
    { path: '/transactions', icon: CreditCard, label: t('transactions') },
    { path: '/chat', icon: MessageSquare, label: t('chat') },
    { path: '/profile', icon: User, label: t('profile') },
  ];
  
  // Add admin link for admin users
  if (user?.role === 'admin') {
    navItems.push({ path: '/admin', icon: Settings, label: t('admin') });
    navItems.push({ path: '/admin/whatsapp', icon: MessageCircle, label: t('whatsappConfig') });
  }
  
  const handleLogout = async () => {
    await logout();
    navigate('/');
  };
  
  return (
    <div className="min-h-screen bg-[#0a0a0f] pb-16 md:pb-0">
      {/* Header */}
      <header className="sticky top-0 z-40 glass-dark">
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="flex items-center gap-2">
              <img src="/logo.jpg" alt="LeadMotiv Hayti" className="h-10 w-auto" />
              <span className="text-yellow-400 font-bold text-lg hidden sm:inline">MAGAZEN LETA</span>
            </Link>
            {user && (
              <span className="hidden md:inline-block magazen-id text-sm bg-yellow-500/10 px-2 py-1 rounded border border-yellow-500/30">
                {user.magazen_id}
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <LanguageSelector />
            <ThemeToggle />
            <NotificationBell />
            <button
              data-testid="mobile-menu-btn"
              className="md:hidden p-2 hover:bg-white/10 rounded-lg text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <button
              data-testid="logout-btn"
              onClick={handleLogout}
              className="hidden md:flex items-center gap-2 text-gray-400 hover:text-red-400 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span className="text-sm font-medium">{t('logout')}</span>
            </button>
          </div>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-[#2d2d3a] bg-[#12121a]">
            <nav className="px-4 py-2">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg ${
                    location.pathname === item.path ? 'bg-red-500/20 text-red-400' : 'text-gray-400'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              ))}
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-3 text-red-400"
              >
                <LogOut className="w-5 h-5" />
                <span className="font-medium">{t('logout')}</span>
              </button>
            </nav>
          </div>
        )}
      </header>
      
      {/* Desktop Sidebar + Content */}
      <div className="max-w-7xl mx-auto flex">
        {/* Sidebar - Desktop */}
        <aside className="hidden md:block w-64 min-h-[calc(100vh-64px)] bg-[#12121a] border-r border-[#2d2d3a] p-4">
          <nav className="space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  location.pathname === item.path
                    ? 'bg-red-500/20 text-red-400 font-semibold'
                    : 'text-gray-400 hover:bg-white/5'
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </Link>
            ))}
          </nav>
        </aside>
        
        {/* Main Content */}
        <main className="flex-1 p-4 md:p-6">
          {children}
        </main>
      </div>
      
      {/* Bottom Navigation - Mobile */}
      <nav className="bottom-nav md:hidden">
        {navItems.slice(0, 4).map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`bottom-nav-item ${location.pathname === item.path ? 'active' : ''}`}
          >
            <item.icon className="w-5 h-5" />
            <span className="text-xs">{item.label}</span>
          </Link>
        ))}
      </nav>
    </div>
  );
};

// Dashboard Page
const DashboardPage = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [matrices, setMatrices] = useState([]);
  const [selectedMatrix, setSelectedMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddMember, setShowAddMember] = useState(false);
  const [report, setReport] = useState(null);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, matricesRes] = await Promise.all([
          axios.get(`${API}/stats`),
          axios.get(`${API}/matrix/list`)
        ]);
        setStats(statsRes.data);
        setMatrices(matricesRes.data);
        if (matricesRes.data.length > 0) {
          setSelectedMatrix(matricesRes.data[0]);
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);
  
  const handleCreateMatrix = async () => {
    try {
      const response = await axios.post(`${API}/matrix/create`, { name: `Matrix ${matrices.length + 1}` });
      setMatrices([...matrices, response.data]);
      setSelectedMatrix(response.data);
      toast.success('Matrix created!');
    } catch (error) {
      toast.error('Failed to create matrix');
    }
  };
  
  const handlePayment = async () => {
    try {
      const response = await axios.post(`${API}/payments/checkout/session`, {
        origin_url: window.location.origin
      });
      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Payment initiation failed');
    }
  };
  
  const handleGetReport = async () => {
    try {
      const response = await axios.get(`${API}/reports/my-report`);
      setReport(response.data);
      toast.success('Report generated!');
    } catch (error) {
      toast.error('Failed to generate report');
    }
  };
  
  const handleSendEmail = async () => {
    try {
      const response = await axios.post(`${API}/reports/send-email`);
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Failed to send email');
    }
  };
  
  const handleShareWhatsApp = async () => {
    try {
      const response = await axios.get(`${API}/reports/whatsapp-text`);
      const url = `https://wa.me/?text=${encodeURIComponent(response.data.text)}`;
      window.open(url, '_blank');
    } catch (error) {
      toast.error('Failed to generate WhatsApp message');
    }
  };
  
  if (loading) {
    return (
      <DashboardLayout>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="card-dark">
              <div className="skeleton h-4 w-24 mb-2"></div>
              <div className="skeleton h-8 w-16"></div>
            </div>
          ))}
        </div>
      </DashboardLayout>
    );
  }
  
  return (
    <DashboardLayout>
      {/* Sponsor Info */}
      {stats?.sponsor && (
        <div className="card-dark mb-6 flex items-center gap-4">
          <div className="w-12 h-12 bg-yellow-500/20 rounded-full flex items-center justify-center">
            <User className="w-6 h-6 text-yellow-400" />
          </div>
          <div>
            <p className="text-gray-400 text-sm">{t('sponsor')}</p>
            <p className="text-white font-semibold">{stats.sponsor.name}</p>
            <p className="magazen-id text-xs">{stats.sponsor.magazen_id}</p>
          </div>
        </div>
      )}
      
      {/* Matrix Selector */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <select
          data-testid="matrix-selector"
          value={selectedMatrix?.matrix_id || ''}
          onChange={(e) => setSelectedMatrix(matrices.find(m => m.matrix_id === e.target.value))}
          className="input-dark max-w-xs"
        >
          {matrices.map(m => (
            <option key={m.matrix_id} value={m.matrix_id}>
              {m.name} ({m.magazen_matrix_id})
            </option>
          ))}
        </select>
        
        <button
          data-testid="create-matrix-btn"
          onClick={handleCreateMatrix}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          {t('createMatrix')}
        </button>
      </div>
      
      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <div className="stat-card red">
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
            <Users className="w-4 h-4" />
            <span>{t('totalMembers')}</span>
          </div>
          <div className="metric-value">{selectedMatrix?.total_members || 0}</div>
          <div className="text-xs text-gray-500 mt-1">/ 1,554 max</div>
        </div>
        
        <div className="stat-card gold">
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
            <TrendingUp className="w-4 h-4" />
            <span>{t('totalPW')}</span>
          </div>
          <div className="metric-value">{selectedMatrix?.total_pw || 50}</div>
          <div className="text-xs text-gray-500 mt-1">50 PW/manm</div>
        </div>
        
        <div className="stat-card green">
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
            <Award className="w-4 h-4" />
            <span>{t('currentRank')}</span>
          </div>
          <div className={`rank-badge nivo-${selectedMatrix?.current_rank || 0} text-sm mt-2`}>
            {selectedMatrix?.rank_name || t('noRank')}
          </div>
        </div>
        
        <div className="stat-card blue">
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
            <Users className="w-4 h-4" />
            <span>{t('directRecruits')}</span>
          </div>
          <div className="text-3xl font-bold text-blue-400">{stats?.direct_recruits || 0}</div>
        </div>
        
        <div className="stat-card gold">
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
            <DollarSign className="w-4 h-4" />
            <span>{t('totalEarnings')}</span>
          </div>
          <div className="text-2xl font-bold text-green-400">{(stats?.total_earnings || 0).toLocaleString()}</div>
        </div>
      </div>
      
      {/* Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <button
          data-testid="add-member-btn"
          onClick={() => setShowAddMember(true)}
          className="card-dark flex items-center justify-center gap-3 hover:border-red-500 transition-colors cursor-pointer"
        >
          <Plus className="w-6 h-6 text-red-400" />
          <span className="font-semibold text-white">{t('addMember')}</span>
        </button>
        
        <button
          data-testid="pay-subscription-btn"
          onClick={handlePayment}
          className="btn-gold flex items-center justify-center gap-3"
        >
          <CreditCard className="w-5 h-5" />
          <span>{t('paySubscription')}</span>
        </button>
        
        <button
          data-testid="weekly-report-btn"
          onClick={handleGetReport}
          className="btn-green flex items-center justify-center gap-3"
        >
          <FileText className="w-5 h-5" />
          <span>{t('weeklyReport')}</span>
        </button>
      </div>
      
      {/* Weekly Report Section */}
      {report && (
        <div className="card-dark mb-6 animate-slide-up">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-yellow-400" />
            {t('weeklyReport')}
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="bg-[#1a1a25] rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-yellow-400">{report.total_members}</div>
              <div className="text-xs text-gray-400">Total Manm</div>
            </div>
            <div className="bg-[#1a1a25] rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-green-400">+{report.new_members_this_week}</div>
              <div className="text-xs text-gray-400">Nouvo Semen Sa</div>
            </div>
            <div className="bg-[#1a1a25] rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-blue-400">{report.total_pw}</div>
              <div className="text-xs text-gray-400">Total PW</div>
            </div>
            <div className="bg-[#1a1a25] rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-purple-400">{report.direct_recruits}</div>
              <div className="text-xs text-gray-400">Rekritman Dirèk</div>
            </div>
          </div>
          
          <div className="flex gap-3">
            <button
              data-testid="send-email-btn"
              onClick={handleSendEmail}
              className="btn-primary flex-1 flex items-center justify-center gap-2"
            >
              <Mail className="w-4 h-4" />
              {t('sendEmail')}
            </button>
            <button
              data-testid="share-whatsapp-report"
              onClick={handleShareWhatsApp}
              className="btn-whatsapp flex-1"
            >
              <Share2 className="w-4 h-4" />
              {t('shareWhatsApp')}
            </button>
          </div>
        </div>
      )}
      
      {/* Rank Progress */}
      <div className="card-dark">
        <h3 className="text-lg font-bold text-white mb-4">Rank Progress</h3>
        <div className="space-y-4">
          {[
            { level: 1, name: 'Nivo 1 - Lidè kap vini', members: 6, pw: 350, bonus: 25000 },
            { level: 2, name: 'Nivo 2 - Lidè konfime', members: 42, pw: 2150, bonus: 200000 },
            { level: 3, name: 'Nivo 3 - Lidè eksperyans', members: 258, pw: 12950, bonus: 300000 },
            { level: 4, name: 'Nivo 4 - Nivo LO', members: 1554, pw: 77750, bonus: 1500000 },
          ].map((rank) => {
            const achieved = (selectedMatrix?.current_rank || 0) >= rank.level;
            const memberProgress = Math.min(100, ((selectedMatrix?.total_members || 0) / rank.members) * 100);
            
            return (
              <div key={rank.level} className={`p-4 rounded-lg border ${achieved ? 'bg-green-900/20 border-green-500/50' : 'bg-[#1a1a25] border-[#2d2d3a]'}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className={`rank-badge nivo-${rank.level}`}>{rank.name}</span>
                    {achieved && <span className="text-green-400 text-sm font-bold">✓</span>}
                  </div>
                  <span className="text-yellow-400 font-bold">{rank.bonus.toLocaleString()} HTG</span>
                </div>
                {!achieved && (
                  <div className="mt-2">
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>{selectedMatrix?.total_members || 0} / {rank.members} manm</span>
                      <span>{Math.round(memberProgress)}%</span>
                    </div>
                    <div className="h-2 bg-[#2d2d3a] rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-red-500 to-yellow-500 transition-all duration-500"
                        style={{ width: `${memberProgress}%` }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Add Member Modal */}
      {showAddMember && (
        <AddMemberModal
          matrixId={selectedMatrix?.matrix_id}
          onClose={() => setShowAddMember(false)}
          onSuccess={() => {
            setShowAddMember(false);
            axios.get(`${API}/matrix/list`).then(res => {
              setMatrices(res.data);
              const updated = res.data.find(m => m.matrix_id === selectedMatrix?.matrix_id);
              if (updated) setSelectedMatrix(updated);
            });
            axios.get(`${API}/stats`).then(res => setStats(res.data));
          }}
        />
      )}
    </DashboardLayout>
  );
};

// Add Member Modal
const AddMemberModal = ({ matrixId, onClose, onSuccess }) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      await axios.post(`${API}/matrix/${matrixId}/add-member`, {
        ...formData,
        matrix_id: matrixId
      });
      toast.success('Member added successfully!');
      onSuccess();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to add member');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
      <div className="card-dark w-full max-w-md animate-slide-up">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white">{t('addMember')}</h3>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg text-gray-400">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {error && (
          <div className="bg-red-900/50 border border-red-500 text-red-300 px-4 py-3 rounded-lg mb-4 text-sm">{error}</div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1">{t('name')}</label>
            <input
              data-testid="add-member-name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input-dark"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1">{t('email')}</label>
            <input
              data-testid="add-member-email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="input-dark"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1">{t('phone')}</label>
            <input
              data-testid="add-member-phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="input-dark"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1">{t('password')}</label>
            <input
              data-testid="add-member-password"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="input-dark"
              required
              minLength={6}
            />
          </div>
          
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-[#2d2d3a] text-gray-400 rounded-lg hover:bg-white/5">
              {t('cancel')}
            </button>
            <button
              data-testid="add-member-submit"
              type="submit"
              disabled={loading}
              className="flex-1 btn-primary"
            >
              {loading ? t('loading') : t('addMember')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Genealogy Page
const GenealogyPage = () => {
  const { t } = useLanguage();
  const [matrices, setMatrices] = useState([]);
  const [selectedMatrix, setSelectedMatrix] = useState(null);
  const [genealogy, setGenealogy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  
  useEffect(() => {
    const fetchMatrices = async () => {
      try {
        const response = await axios.get(`${API}/matrix/list`);
        setMatrices(response.data);
        if (response.data.length > 0) {
          setSelectedMatrix(response.data[0]);
        }
      } catch (error) {
        console.error('Failed to fetch matrices:', error);
      }
    };
    fetchMatrices();
  }, []);
  
  useEffect(() => {
    const fetchGenealogy = async () => {
      if (!selectedMatrix) return;
      setLoading(true);
      try {
        const response = await axios.get(`${API}/matrix/${selectedMatrix.matrix_id}/genealogy`);
        setGenealogy(response.data);
      } catch (error) {
        console.error('Failed to fetch genealogy:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchGenealogy();
  }, [selectedMatrix]);

  const handleSearch = async () => {
    if (!selectedMatrix || !searchQuery.trim()) {
      setSearchResults(null);
      return;
    }
    setSearching(true);
    try {
      const response = await axios.get(`${API}/matrix/${selectedMatrix.matrix_id}/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchResults(response.data);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setSearching(false);
    }
  };
  
  return (
    <DashboardLayout>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white mb-4">{t('genealogy')}</h2>
        
        <div className="flex flex-col sm:flex-row gap-3">
          <select
            data-testid="genealogy-matrix-selector"
            value={selectedMatrix?.matrix_id || ''}
            onChange={(e) => { setSelectedMatrix(matrices.find(m => m.matrix_id === e.target.value)); setSearchResults(null); setSearchQuery(''); }}
            className="input-dark max-w-xs"
          >
            {matrices.map(m => (
              <option key={m.matrix_id} value={m.matrix_id}>
                {m.name} ({m.magazen_matrix_id})
              </option>
            ))}
          </select>

          <div className="flex gap-2 flex-1 max-w-md">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input
                data-testid="genealogy-search"
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder={t('searchGenealogy')}
                className="input-dark pl-10 text-sm"
              />
            </div>
            <button onClick={handleSearch} className="btn-primary px-3" disabled={searching}>
              <Filter className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Search Results */}
      {searchResults && (
        <div className="card-dark mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-400">{searchResults.length} result(s)</h3>
            <button onClick={() => { setSearchResults(null); setSearchQuery(''); }} className="text-xs text-red-400 hover:text-red-300">Clear</button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {searchResults.map((r, i) => (
              <div key={i} className="bg-[#1a1a2e] rounded-lg p-3 text-center">
                <div className="text-white text-sm font-medium truncate">{r.name}</div>
                <div className="text-[10px] magazen-id">{r.magazen_id}</div>
                <div className="text-[10px] text-gray-500 truncate">{r.email}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {loading ? (
        <div className="card-dark flex items-center justify-center py-12">
          <div className="w-10 h-10 border-4 border-red-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : genealogy ? (
        <div className="card-dark overflow-x-auto">
          <div className="tree-container">
            {/* Root Node */}
            <div className="tree-level">
              <div className="tree-node owner animate-glow">
                <div className="font-bold text-yellow-400 text-sm">{genealogy.root.name}</div>
                <div className="text-xs magazen-id">{genealogy.root.magazen_id}</div>
                <div className="text-xs text-gray-500 mt-1">WOU (You)</div>
              </div>
            </div>
            
            {/* Levels */}
            {genealogy.levels.map((level, idx) => (
              <div key={idx} className="mb-4">
                <div className="text-center mb-2">
                  <span className={`rank-badge nivo-${level.level}`}>
                    {t('level')} {level.level}
                  </span>
                </div>
                <div className="tree-level flex-wrap">
                  {level.members.slice(0, Math.min(level.members.length, 12)).map((member, mIdx) => (
                    <div 
                      key={mIdx} 
                      className={`tree-node ${member.empty ? 'empty' : ''}`}
                    >
                      {member.empty ? (
                        <div className="text-gray-600 text-xs">{t('emptySlot')}</div>
                      ) : (
                        <>
                          <div className="font-semibold text-white text-xs truncate max-w-[70px]">
                            {member.name}
                          </div>
                          <div className="text-[10px] magazen-id truncate">
                            {member.magazen_id}
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                  {level.members.length > 12 && (
                    <div className="text-sm text-gray-500 self-center">
                      +{level.members.length - 12} more
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="card-dark text-center py-8 text-gray-500">
          No genealogy data available
        </div>
      )}
    </DashboardLayout>
  );
};

// Chat Page
const ChatPage = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  
  useEffect(() => {
    const fetchConversations = async () => {
      try {
        const response = await axios.get(`${API}/chat/conversations`);
        setConversations(response.data);
      } catch (error) {
        console.error('Failed to fetch conversations:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchConversations();
  }, []);
  
  useEffect(() => {
    const fetchMessages = async () => {
      if (!selectedConversation) return;
      try {
        const response = await axios.get(`${API}/chat/messages/${selectedConversation.partner_id}`);
        setMessages(response.data);
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      } catch (error) {
        console.error('Failed to fetch messages:', error);
      }
    };
    fetchMessages();
    
    const interval = setInterval(fetchMessages, 5000);
    return () => clearInterval(interval);
  }, [selectedConversation]);
  
  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;
    setSending(true);
    
    try {
      await axios.post(`${API}/chat/send`, {
        receiver_id: selectedConversation.partner_id,
        message: newMessage
      });
      setNewMessage('');
      const response = await axios.get(`${API}/chat/messages/${selectedConversation.partner_id}`);
      setMessages(response.data);
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };
  
  return (
    <DashboardLayout>
      <div className="h-[calc(100vh-200px)] md:h-[calc(100vh-150px)] flex flex-col md:flex-row gap-4">
        {/* Conversations List */}
        <div className={`w-full md:w-80 card-dark overflow-hidden ${selectedConversation ? 'hidden md:block' : ''}`}>
          <div className="p-4 border-b border-[#2d2d3a]">
            <h3 className="font-bold text-white">{t('conversations')}</h3>
          </div>
          
          {loading ? (
            <div className="p-4 space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="flex gap-3">
                  <div className="skeleton w-10 h-10 rounded-full"></div>
                  <div className="flex-1">
                    <div className="skeleton h-4 w-24 mb-2"></div>
                    <div className="skeleton h-3 w-32"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : conversations.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              {t('noConversations')}
            </div>
          ) : (
            <div className="overflow-y-auto max-h-[400px]">
              {conversations.map((conv) => (
                <button
                  key={conv.partner_id}
                  onClick={() => setSelectedConversation(conv)}
                  className={`w-full p-4 flex items-center gap-3 hover:bg-white/5 transition-colors ${
                    selectedConversation?.partner_id === conv.partner_id ? 'bg-red-500/20' : ''
                  }`}
                >
                  <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-red-400" />
                  </div>
                  <div className="flex-1 text-left">
                    <div className="font-semibold text-white text-sm">{conv.partner_name}</div>
                    <div className="text-xs text-gray-500 truncate">{conv.last_message || 'No messages'}</div>
                  </div>
                  {conv.unread_count > 0 && (
                    <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                      {conv.unread_count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
        
        {/* Chat Area */}
        <div className={`flex-1 card-dark flex flex-col ${!selectedConversation ? 'hidden md:flex' : ''}`}>
          {selectedConversation ? (
            <>
              <div className="p-4 border-b border-[#2d2d3a] flex items-center gap-3">
                <button
                  onClick={() => setSelectedConversation(null)}
                  className="md:hidden p-2 hover:bg-white/10 rounded-lg text-gray-400"
                >
                  <X className="w-5 h-5" />
                </button>
                <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-red-400" />
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-white">{selectedConversation.partner_name}</div>
                  <div className="text-xs magazen-id">{selectedConversation.partner_magazen_id}</div>
                </div>
              </div>
              
              <div className="flex-1 overflow-y-auto p-4 space-y-2">
                {messages.map((msg) => (
                  <div
                    key={msg.message_id}
                    className={`chat-bubble ${msg.sender_id === user?.user_id ? 'sent' : 'received'}`}
                  >
                    <p className="text-sm text-white">{msg.message}</p>
                    <span className="text-[10px] text-gray-500 mt-1 block">
                      {new Date(msg.created_at).toLocaleTimeString()}
                    </span>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
              
              <div className="p-4 border-t border-[#2d2d3a]">
                <div className="flex gap-2">
                  <input
                    data-testid="chat-input"
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                    placeholder={t('typeMessage')}
                    className="input-dark flex-1"
                  />
                  <button
                    data-testid="send-message-btn"
                    onClick={sendMessage}
                    disabled={sending || !newMessage.trim()}
                    className="btn-primary px-4"
                  >
                    {sending ? '...' : <Send className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-500">
              Select a conversation to start chatting
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

// Profile Page
const ProfilePage = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [matrices, setMatrices] = useState([]);
  const [referralLink, setReferralLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [stats, setStats] = useState(null);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [matricesRes, statsRes] = await Promise.all([
          axios.get(`${API}/matrix/list`),
          axios.get(`${API}/stats`)
        ]);
        setMatrices(matricesRes.data);
        setStats(statsRes.data);
        // Build referral link using user's MAGAZEN ID
        if (user?.magazen_id) {
          setReferralLink(`${window.location.origin}/register?ref=${user.magazen_id}`);
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
    };
    fetchData();
  }, [user]);
  
  const copyReferralLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    toast.success(t('copied'));
    setTimeout(() => setCopied(false), 2000);
  };
  
  const shareWhatsApp = () => {
    const text = `Rejwenn rezo MAGAZEN mwen an epi touche jiska 1.5M HTG! ${referralLink}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };
  
  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto">
        <div className="card-dark mb-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-20 h-20 bg-gradient-to-br from-red-500 to-yellow-500 rounded-full flex items-center justify-center">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-full h-full rounded-full object-cover" />
              ) : (
                <User className="w-10 h-10 text-white" />
              )}
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{user?.name}</h2>
              <p className="text-gray-400">{user?.email}</p>
              <p className="magazen-id text-sm mt-1">{user?.magazen_id}</p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-[#1a1a25] rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-yellow-400">{matrices.length}</div>
              <div className="text-sm text-gray-500">Matrices</div>
            </div>
            <div className="bg-[#1a1a25] rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-green-400">
                {matrices.reduce((sum, m) => sum + (m.total_members || 0), 0)}
              </div>
              <div className="text-sm text-gray-500">Total Manm</div>
            </div>
            <div className="bg-[#1a1a25] rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-blue-400">{stats?.direct_recruits || 0}</div>
              <div className="text-sm text-gray-500">Rekritman</div>
            </div>
            <div className="bg-[#1a1a25] rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-purple-400">{stats?.total_pw || 0}</div>
              <div className="text-sm text-gray-500">Total PW</div>
            </div>
          </div>
          
          {stats?.sponsor && (
            <div className="bg-[#1a1a25] rounded-lg p-4 mb-4">
              <p className="text-gray-400 text-sm mb-1">{t('sponsor')}</p>
              <p className="text-white font-semibold">{stats.sponsor.name}</p>
              <p className="magazen-id text-xs">{stats.sponsor.magazen_id}</p>
            </div>
          )}
        </div>
        
        {/* Referral Section */}
        <div className="card-dark">
          <h3 className="font-bold text-white mb-4 flex items-center gap-2">
            <Share2 className="w-5 h-5 text-yellow-400" />
            {t('referralLink')}
          </h3>
          
          <div className="bg-[#1a1a25] rounded-lg p-3 mb-4">
            <input
              data-testid="referral-link"
              type="text"
              value={referralLink}
              readOnly
              className="w-full bg-transparent text-sm text-gray-400 outline-none"
            />
          </div>
          
          <div className="flex gap-3">
            <button
              data-testid="copy-referral-btn"
              onClick={copyReferralLink}
              className="flex-1 btn-primary"
            >
              {copied ? t('copied') : 'Copy Link'}
            </button>
            <button
              data-testid="whatsapp-share-btn"
              onClick={shareWhatsApp}
              className="btn-whatsapp flex-1"
            >
              <Phone className="w-5 h-5" />
              {t('shareWhatsApp')}
            </button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

// Admin Page
const AdminPage = () => {
  const { t } = useLanguage();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', phone: '', email: '', sponsor_id: '' });
  
  useEffect(() => {
    fetchData();
  }, []);
  
  const fetchData = async () => {
    try {
      const [usersRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/users?limit=50`),
        axios.get(`${API}/admin/stats`)
      ]);
      setUsers(usersRes.data.users);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSearch = async () => {
    try {
      const response = await axios.get(`${API}/admin/users?search=${search}`);
      setUsers(response.data.users);
    } catch (error) {
      console.error('Search failed:', error);
    }
  };
  
  const openEditModal = (user) => {
    setEditingUser(user);
    setEditForm({ name: user.name, phone: user.phone || '', email: user.email, sponsor_id: user.sponsor_magazen_id || '' });
  };
  
  const handleUpdateUser = async () => {
    try {
      await axios.put(`${API}/admin/users/${editingUser.user_id}`, editForm);
      toast.success('User updated!');
      setEditingUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Update failed');
    }
  };

  const handleDeleteUser = async (user) => {
    if (user.role === 'admin') {
      toast.error('Cannot delete admin accounts');
      return;
    }
    if (!window.confirm(`Delete user ${user.name} (${user.magazen_id})? This action cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${user.user_id}`);
      toast.success(`User ${user.magazen_id} deleted`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };
  
  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-12">
          <div className="w-10 h-10 border-4 border-red-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }
  
  return (
    <DashboardLayout>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-white">{t('admin')}</h2>
        <Link
          to="/admin/whatsapp"
          data-testid="admin-whatsapp-link"
          className="flex items-center gap-2 px-4 py-2 bg-[#25D366]/20 text-[#25D366] rounded-lg hover:bg-[#25D366]/30 transition-colors border border-[#25D366]/30"
        >
          <MessageCircle className="w-5 h-5" />
          {t('whatsappConfig')}
        </Link>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="stat-card red">
          <div className="text-gray-400 text-sm">Total Users</div>
          <div className="text-2xl font-bold text-white">{stats?.total_users || 0}</div>
        </div>
        <div className="stat-card green">
          <div className="text-gray-400 text-sm">Active Users</div>
          <div className="text-2xl font-bold text-green-400">{stats?.active_users || 0}</div>
        </div>
        <div className="stat-card gold">
          <div className="text-gray-400 text-sm">Total Matrices</div>
          <div className="text-2xl font-bold text-yellow-400">{stats?.total_matrices || 0}</div>
        </div>
        <div className="stat-card blue">
          <div className="text-gray-400 text-sm">Recent Signups (7d)</div>
          <div className="text-2xl font-bold text-blue-400">{stats?.recent_signups || 0}</div>
        </div>
      </div>
      
      {/* Users Table */}
      <div className="card-dark">
        <div className="flex items-center gap-4 mb-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <input
              data-testid="admin-search"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              placeholder={t('searchUsers')}
              className="input-dark pl-10"
            />
          </div>
          <button onClick={handleSearch} className="btn-primary">
            <Search className="w-4 h-4" />
          </button>
        </div>
        
        <div className="overflow-x-auto">
          <table className="admin-table">
            <thead>
              <tr>
                <th>MAGAZEN ID</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Role</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.user_id}>
                  <td className="magazen-id text-sm">{user.magazen_id}</td>
                  <td className="text-white">{user.name}</td>
                  <td className="text-gray-400">{user.email}</td>
                  <td className="text-gray-400">{user.phone || '-'}</td>
                  <td>
                    <span className={`px-2 py-1 rounded text-xs ${user.role === 'admin' ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'}`}>
                      {user.role}
                    </span>
                  </td>
                  <td>
                    <div className="flex items-center gap-1">
                      <button
                        data-testid={`edit-user-${user.user_id}`}
                        onClick={() => openEditModal(user)}
                        className="p-2 hover:bg-white/10 rounded-lg text-gray-400 hover:text-white"
                        title={t('editUser')}
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      {user.role !== 'admin' && (
                        <button
                          data-testid={`delete-user-${user.user_id}`}
                          onClick={() => handleDeleteUser(user)}
                          className="p-2 hover:bg-red-500/10 rounded-lg text-gray-400 hover:text-red-400"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Edit Modal */}
      {editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <div className="card-dark w-full max-w-md animate-slide-up">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">{t('editUser')}</h3>
              <button onClick={() => setEditingUser(null)} className="p-2 hover:bg-white/10 rounded-lg text-gray-400">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">MAGAZEN ID</label>
                <input
                  type="text"
                  value={editingUser.magazen_id}
                  disabled
                  className="input-dark opacity-50"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">{t('name')}</label>
                <input
                  data-testid="edit-user-name"
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="input-dark"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">{t('email')}</label>
                <input
                  data-testid="edit-user-email"
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="input-dark"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">{t('phone')}</label>
                <input
                  data-testid="edit-user-phone"
                  type="tel"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  className="input-dark"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">{t('sponsorId')}</label>
                <input
                  data-testid="edit-user-sponsor"
                  type="text"
                  value={editForm.sponsor_id}
                  onChange={(e) => setEditForm({ ...editForm, sponsor_id: e.target.value.toUpperCase() })}
                  placeholder="MAGAZEN-XXXXX"
                  className="input-dark"
                />
                <p className="text-xs text-gray-600 mt-1">{t('sponsorId')} MAGAZEN ID</p>
              </div>
              
              <div className="flex gap-3">
                <button onClick={() => setEditingUser(null)} className="flex-1 px-4 py-2 border border-[#2d2d3a] text-gray-400 rounded-lg hover:bg-white/5">
                  {t('cancel')}
                </button>
                <button
                  data-testid="save-user-btn"
                  onClick={handleUpdateUser}
                  className="flex-1 btn-primary"
                >
                  {t('save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

// Payment Pages
const PaymentSuccessPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState('checking');
  
  useEffect(() => {
    const checkPayment = async () => {
      const params = new URLSearchParams(location.search);
      const sessionId = params.get('session_id');
      
      if (!sessionId) {
        setStatus('error');
        return;
      }
      
      let attempts = 0;
      const poll = async () => {
        try {
          const response = await axios.get(`${API}/payments/checkout/status/${sessionId}`);
          if (response.data.payment_status === 'paid') {
            setStatus('success');
          } else if (attempts < 5) {
            attempts++;
            setTimeout(poll, 2000);
          } else {
            setStatus('pending');
          }
        } catch (error) {
          setStatus('error');
        }
      };
      poll();
    };
    checkPayment();
  }, [location]);
  
  return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center p-4">
      <div className="card-dark max-w-md w-full text-center">
        {status === 'checking' && (
          <>
            <div className="w-16 h-16 border-4 border-red-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-400">Checking payment status...</p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
            <h2 className="text-xl font-bold text-green-400 mb-2">{t('paymentSuccess')}</h2>
            <p className="text-gray-400 mb-6">Your subscription is now active!</p>
            <button onClick={() => navigate('/dashboard')} className="btn-primary">
              Go to Dashboard
            </button>
          </>
        )}
        
        {status === 'pending' && (
          <>
            <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-yellow-400" />
            </div>
            <h2 className="text-xl font-bold text-yellow-400 mb-2">Payment Processing</h2>
            <p className="text-gray-400 mb-6">Your payment is being processed.</p>
            <button onClick={() => navigate('/dashboard')} className="btn-primary">
              Go to Dashboard
            </button>
          </>
        )}
        
        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <X className="w-8 h-8 text-red-400" />
            </div>
            <h2 className="text-xl font-bold text-red-400 mb-2">Payment Error</h2>
            <p className="text-gray-400 mb-6">Something went wrong. Please try again.</p>
            <button onClick={() => navigate('/dashboard')} className="btn-primary">
              Go to Dashboard
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const PaymentCancelPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center p-4">
      <div className="card-dark max-w-md w-full text-center">
        <div className="w-16 h-16 bg-gray-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <X className="w-8 h-8 text-gray-400" />
        </div>
        <h2 className="text-xl font-bold text-white mb-2">{t('paymentCancel')}</h2>
        <p className="text-gray-400 mb-6">Your payment was cancelled.</p>
        <button onClick={() => navigate('/dashboard')} className="btn-primary">
          Go to Dashboard
        </button>
      </div>
    </div>
  );
};

// Floating WhatsApp Button (appears on all pages)
const FloatingWhatsAppButton = () => {
  const { t } = useLanguage();
  const [whatsappData, setWhatsappData] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [accounts, setAccounts] = useState([]);
  
  useEffect(() => {
    const fetchWhatsApp = async () => {
      try {
        const [defaultRes, accountsRes] = await Promise.all([
          axios.get(`${API}/whatsapp/default`),
          axios.get(`${API}/whatsapp/accounts`)
        ]);
        setWhatsappData(defaultRes.data);
        setAccounts(accountsRes.data);
      } catch (error) {
        console.error('Failed to fetch WhatsApp config:', error);
      }
    };
    fetchWhatsApp();
  }, []);
  
  const openWhatsApp = (phone) => {
    const message = encodeURIComponent("Bonjou! Mwen bezwen èd avèk MAGAZEN LETA.");
    window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
  };
  
  if (!whatsappData?.phone_number && accounts.length === 0) return null;
  
  return (
    <div className="fixed bottom-20 md:bottom-6 right-4 z-50">
      {showMenu && accounts.length > 1 && (
        <div className="absolute bottom-16 right-0 bg-[#12121a] rounded-xl border border-[#2d2d3a] shadow-xl p-2 mb-2 min-w-[200px] animate-slide-up">
          <p className="text-xs text-gray-400 px-3 py-1">{t('needHelp')}</p>
          {accounts.map((acc) => (
            <button
              key={acc.account_id}
              onClick={() => openWhatsApp(acc.phone_number)}
              className="w-full flex items-center gap-3 px-3 py-2 hover:bg-white/10 rounded-lg text-left"
            >
              <div className="w-8 h-8 bg-[#25D366] rounded-full flex items-center justify-center">
                <Phone className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-white text-sm font-medium">{acc.name}</p>
                <p className="text-gray-500 text-xs">{acc.description || acc.phone_number}</p>
              </div>
              {acc.is_default && (
                <span className="ml-auto text-xs text-green-400">{t('default')}</span>
              )}
            </button>
          ))}
        </div>
      )}
      
      <button
        data-testid="whatsapp-help-btn"
        onClick={() => {
          if (accounts.length > 1) {
            setShowMenu(!showMenu);
          } else if (whatsappData?.phone_number) {
            openWhatsApp(whatsappData.phone_number);
          }
        }}
        className="w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg hover:bg-[#1DA851] transition-all hover:scale-110 animate-pulse"
        title={t('needHelp')}
      >
        <svg className="w-7 h-7 text-white" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </button>
    </div>
  );
};

// WhatsApp Admin Page
const WhatsAppAdminPage = () => {
  const { t } = useLanguage();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [formData, setFormData] = useState({ name: '', phone_number: '', description: '', is_active: true, is_default: false });
  
  useEffect(() => {
    fetchAccounts();
  }, []);
  
  const fetchAccounts = async () => {
    try {
      const response = await axios.get(`${API}/whatsapp/admin/accounts`);
      setAccounts(response.data);
    } catch (error) {
      console.error('Failed to fetch accounts:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAccount) {
        await axios.put(`${API}/whatsapp/admin/accounts/${editingAccount.account_id}`, formData);
        toast.success('Account updated!');
      } else {
        await axios.post(`${API}/whatsapp/admin/accounts`, formData);
        toast.success('Account created!');
      }
      setShowAddModal(false);
      setEditingAccount(null);
      setFormData({ name: '', phone_number: '', description: '', is_active: true, is_default: false });
      fetchAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };
  
  const handleDelete = async (accountId) => {
    if (!window.confirm('Delete this WhatsApp account?')) return;
    try {
      await axios.delete(`${API}/whatsapp/admin/accounts/${accountId}`);
      toast.success('Account deleted!');
      fetchAccounts();
    } catch (error) {
      toast.error('Delete failed');
    }
  };
  
  const openEditModal = (account) => {
    setEditingAccount(account);
    setFormData({
      name: account.name,
      phone_number: account.phone_number,
      description: account.description || '',
      is_active: account.is_active,
      is_default: account.is_default
    });
    setShowAddModal(true);
  };
  
  const toggleActive = async (account) => {
    try {
      await axios.put(`${API}/whatsapp/admin/accounts/${account.account_id}`, {
        is_active: !account.is_active
      });
      fetchAccounts();
    } catch (error) {
      toast.error('Update failed');
    }
  };
  
  const setAsDefault = async (account) => {
    try {
      await axios.put(`${API}/whatsapp/admin/accounts/${account.account_id}`, {
        is_default: true
      });
      toast.success(`${account.name} is now default`);
      fetchAccounts();
    } catch (error) {
      toast.error('Update failed');
    }
  };
  
  return (
    <DashboardLayout>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-white flex items-center gap-3">
          <MessageCircle className="w-7 h-7 text-[#25D366]" />
          {t('whatsappAccounts')}
        </h2>
        <button
          data-testid="add-whatsapp-btn"
          onClick={() => {
            setEditingAccount(null);
            setFormData({ name: '', phone_number: '', description: '', is_active: true, is_default: false });
            setShowAddModal(true);
          }}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          {t('addAccount')}
        </button>
      </div>
      
      {loading ? (
        <div className="card-dark flex items-center justify-center py-12">
          <div className="w-10 h-10 border-4 border-[#25D366] border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : accounts.length === 0 ? (
        <div className="card-dark text-center py-12">
          <MessageCircle className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 mb-4">No WhatsApp accounts configured</p>
          <button onClick={() => setShowAddModal(true)} className="btn-primary">
            {t('addAccount')}
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {accounts.map((account) => (
            <div key={account.account_id} className={`card-dark flex items-center gap-4 ${!account.is_active ? 'opacity-50' : ''}`}>
              <div className="w-12 h-12 bg-[#25D366] rounded-full flex items-center justify-center">
                <Phone className="w-6 h-6 text-white" />
              </div>
              
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-white font-semibold">{account.name}</h3>
                  {account.is_default && (
                    <span className="bg-green-500/20 text-green-400 text-xs px-2 py-0.5 rounded-full">{t('default')}</span>
                  )}
                  {!account.is_active && (
                    <span className="bg-red-500/20 text-red-400 text-xs px-2 py-0.5 rounded-full">{t('inactive')}</span>
                  )}
                </div>
                <p className="text-gray-400 text-sm">+{account.phone_number}</p>
                {account.description && (
                  <p className="text-gray-500 text-xs mt-1">{account.description}</p>
                )}
              </div>
              
              <div className="flex items-center gap-2">
                {!account.is_default && (
                  <button
                    onClick={() => setAsDefault(account)}
                    className="p-2 text-gray-400 hover:text-green-400 transition-colors"
                    title={t('setDefault')}
                  >
                    <CheckCircle className="w-5 h-5" />
                  </button>
                )}
                <button
                  onClick={() => toggleActive(account)}
                  className={`p-2 transition-colors ${account.is_active ? 'text-green-400' : 'text-gray-500'}`}
                  title={account.is_active ? t('active') : t('inactive')}
                >
                  {account.is_active ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                </button>
                <button
                  onClick={() => openEditModal(account)}
                  className="p-2 text-gray-400 hover:text-white transition-colors"
                >
                  <Edit className="w-5 h-5" />
                </button>
                <button
                  onClick={() => handleDelete(account.account_id)}
                  className="p-2 text-gray-400 hover:text-red-400 transition-colors"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <div className="card-dark w-full max-w-md animate-slide-up">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">
                {editingAccount ? t('editUser') : t('addAccount')}
              </h3>
              <button onClick={() => { setShowAddModal(false); setEditingAccount(null); }} className="p-2 hover:bg-white/10 rounded-lg text-gray-400">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">{t('name')}</label>
                <input
                  data-testid="whatsapp-name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Support, Sales, etc."
                  className="input-dark"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">{t('phoneNumber')}</label>
                <input
                  data-testid="whatsapp-phone"
                  type="text"
                  value={formData.phone_number}
                  onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                  placeholder="509XXXXXXXX"
                  className="input-dark"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Format: Country code + number (e.g., 509XXXXXXXX)</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">{t('description')}</label>
                <input
                  data-testid="whatsapp-description"
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Available Mon-Fri 9am-5pm"
                  className="input-dark"
                />
              </div>
              
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-green-500 focus:ring-green-500"
                  />
                  <span className="text-gray-300">{t('active')}</span>
                </label>
                
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_default}
                    onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-green-500 focus:ring-green-500"
                  />
                  <span className="text-gray-300">{t('setDefault')}</span>
                </label>
              </div>
              
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => { setShowAddModal(false); setEditingAccount(null); }} className="flex-1 px-4 py-2 border border-[#2d2d3a] text-gray-400 rounded-lg hover:bg-white/5">
                  {t('cancel')}
                </button>
                <button type="submit" className="flex-1 btn-whatsapp">
                  <Phone className="w-4 h-4" />
                  {t('save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

// P2: Transaction History Page
const TransactionHistoryPage = () => {
  const { t } = useLanguage();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        const response = await axios.get(`${API}/payments/history`);
        setTransactions(response.data);
      } catch (error) {
        console.error('Failed to fetch transactions:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTransactions();
  }, []);

  const statusColor = (status) => {
    if (status === 'completed' || status === 'paid') return 'text-green-400 bg-green-500/20';
    if (status === 'failed') return 'text-red-400 bg-red-500/20';
    return 'text-yellow-400 bg-yellow-500/20';
  };

  return (
    <DashboardLayout>
      <h2 data-testid="transactions-title" className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
        <CreditCard className="w-7 h-7 text-yellow-400" />
        {t('transactionHistory')}
      </h2>

      {loading ? (
        <div className="card-dark flex items-center justify-center py-12">
          <div className="w-10 h-10 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : transactions.length === 0 ? (
        <div className="card-dark text-center py-12">
          <CreditCard className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400">{t('noTransactions')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {transactions.map((tx, i) => (
            <div key={tx.session_id || i} data-testid={`transaction-${i}`} className="card-dark flex items-center gap-4">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${tx.payment_status === 'completed' || tx.payment_status === 'paid' ? 'bg-green-500/20' : 'bg-yellow-500/20'}`}>
                <DollarSign className={`w-5 h-5 ${tx.payment_status === 'completed' || tx.payment_status === 'paid' ? 'text-green-400' : 'text-yellow-400'}`} />
              </div>
              <div className="flex-1">
                <p className="text-white font-medium">
                  {tx.htg_amount ? `${tx.htg_amount.toLocaleString()} HTG` : `$${tx.amount}`}
                </p>
                <p className="text-gray-500 text-xs">{tx.created_at ? new Date(tx.created_at).toLocaleDateString() : ''}</p>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColor(tx.payment_status)}`}>
                {tx.payment_status === 'completed' || tx.payment_status === 'paid' ? t('completed') : tx.payment_status === 'failed' ? t('failed') : t('pending')}
              </span>
            </div>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
};

// P2: Team Analytics Page
const AnalyticsPage = () => {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const response = await axios.get(`${API}/reports/team-analytics`);
        setData(response.data);
      } catch (error) {
        console.error('Failed to fetch analytics:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, []);

  const handleExportCSV = () => {
    window.open(`${API}/reports/export/csv`, '_blank');
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-12">
          <div className="w-10 h-10 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  const maxGrowth = Math.max(...(data?.weekly_growth?.map(w => w.new_members) || [1]), 1);
  const maxLevel = Math.max(...(data?.level_distribution || [1]), 1);

  return (
    <DashboardLayout>
      <div className="flex items-center justify-between mb-6">
        <h2 data-testid="analytics-title" className="text-2xl font-bold text-white flex items-center gap-3">
          <BarChart3 className="w-7 h-7 text-yellow-400" />
          {t('teamAnalytics')}
        </h2>
        <button data-testid="export-csv-btn" onClick={handleExportCSV} className="flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors border border-green-500/30">
          <Download className="w-4 h-4" />
          {t('exportCSV')}
        </button>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="stat-card red">
          <div className="text-gray-400 text-xs">{t('totalMembers')}</div>
          <div className="text-2xl font-bold text-white">{data?.total_members || 0}</div>
        </div>
        <div className="stat-card gold">
          <div className="text-gray-400 text-xs">{t('totalPW')}</div>
          <div className="text-2xl font-bold text-yellow-400">{data?.total_pw || 0}</div>
        </div>
        <div className="stat-card green">
          <div className="text-gray-400 text-xs">{t('directRecruits')}</div>
          <div className="text-2xl font-bold text-green-400">{data?.direct_recruits_count || 0}</div>
        </div>
        <div className="stat-card blue">
          <div className="text-gray-400 text-xs">{t('totalEarnings')}</div>
          <div className="text-2xl font-bold text-blue-400">{(data?.total_earnings || 0).toLocaleString()} HTG</div>
        </div>
      </div>

      {/* Weekly Growth Chart */}
      <div className="card-dark mb-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-green-400" />
          {t('weeklyGrowth')}
        </h3>
        <div className="flex items-end gap-3 h-40">
          {data?.weekly_growth?.map((week, i) => (
            <div key={i} className="flex-1 flex flex-col items-center gap-2">
              <span className="text-xs text-green-400 font-medium">{week.new_members}</span>
              <div
                className="w-full bg-gradient-to-t from-green-500/80 to-green-400/40 rounded-t-lg transition-all duration-500"
                style={{ height: `${Math.max(8, (week.new_members / maxGrowth) * 100)}%` }}
              ></div>
              <span className="text-xs text-gray-500">{week.week}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Level Distribution */}
      <div className="card-dark mb-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <GitBranch className="w-5 h-5 text-yellow-400" />
          {t('levelDistribution')}
        </h3>
        <div className="space-y-3">
          {data?.level_distribution?.map((count, i) => (
            <div key={i} className="flex items-center gap-3">
              <span className="text-sm text-gray-400 w-16">{t('level')} {i + 1}</span>
              <div className="flex-1 bg-[#1a1a2e] rounded-full h-6 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-yellow-500/80 to-red-500/60 rounded-full flex items-center justify-end pr-2 transition-all duration-500"
                  style={{ width: `${Math.max(5, (count / maxLevel) * 100)}%` }}
                >
                  <span className="text-xs text-white font-medium">{count}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recruit Rank Distribution */}
      <div className="card-dark mb-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Award className="w-5 h-5 text-red-400" />
          {t('directRecruits')} - Ranks
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {data?.recruit_ranks && Object.entries(data.recruit_ranks).map(([key, val]) => (
            <div key={key} className="bg-[#1a1a2e] rounded-lg p-3 text-center">
              <div className="text-xl font-bold text-white">{val}</div>
              <div className="text-xs text-gray-500 capitalize">{key.replace('nivo', 'Nivo ')}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Rank Maintenance Bonuses */}
      {data?.maintenance_bonuses?.length > 0 && (
        <div className="card-dark">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-purple-400" />
            {t('rankMaintenance')}
          </h3>
          <div className="space-y-4">
            {data.maintenance_bonuses.map((mb, i) => (
              <div key={i} className="bg-[#1a1a2e] rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white font-medium">{mb.matrix_name} - {mb.bonus_title}</span>
                  <span className="text-yellow-400 font-bold">{mb.bonus_amount.toLocaleString()} HTG</span>
                </div>
                <div className="flex items-center gap-3 mb-1">
                  <span className="text-xs text-gray-400">{t('consecutiveMonths')}: {mb.consecutive_months}/{mb.required_months}</span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div className="bg-gradient-to-r from-purple-500 to-yellow-400 h-2 rounded-full transition-all" style={{ width: `${mb.progress_pct}%` }}></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

// P3: Notification Bell Component
const NotificationBell = () => {
  const { t } = useLanguage();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const fetchCount = async () => {
      try {
        const res = await axios.get(`${API}/notifications/unread-count`);
        setUnreadCount(res.data.count);
      } catch (e) { /* silent */ }
    };
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const openDropdown = async () => {
    setShowDropdown(!showDropdown);
    if (!showDropdown) {
      try {
        const res = await axios.get(`${API}/notifications`);
        setNotifications(res.data);
      } catch (e) { /* silent */ }
    }
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`);
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (e) { /* silent */ }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button data-testid="notification-bell" onClick={openDropdown} className="relative p-2 hover:bg-white/10 rounded-lg text-gray-400 hover:text-white transition-colors">
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-[10px] text-white flex items-center justify-center font-bold">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {showDropdown && (
        <div className="absolute right-0 top-12 w-80 bg-[#12121a] rounded-xl border border-[#2d2d3a] shadow-2xl z-50 max-h-96 overflow-y-auto">
          <div className="flex items-center justify-between p-3 border-b border-[#2d2d3a]">
            <span className="text-white font-semibold text-sm">{t('notifications')}</span>
            {unreadCount > 0 && (
              <button onClick={markAllRead} className="text-xs text-blue-400 hover:text-blue-300">{t('markAllRead')}</button>
            )}
          </div>
          {notifications.length === 0 ? (
            <div className="p-6 text-center text-gray-500 text-sm">{t('noNotifications')}</div>
          ) : (
            notifications.slice(0, 15).map((n, i) => (
              <div key={n.notification_id || i} className={`p-3 border-b border-[#2d2d3a]/50 hover:bg-white/5 ${!n.is_read ? 'bg-blue-500/5' : ''}`}>
                <p className="text-sm text-white">{n.title || 'Notification'}</p>
                <p className="text-xs text-gray-400 mt-1">{n.message}</p>
                <p className="text-[10px] text-gray-600 mt-1">{n.created_at ? new Date(n.created_at).toLocaleString() : ''}</p>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// P3: Theme Toggle
const ThemeToggle = () => {
  const [theme, setTheme] = useState(() => localStorage.getItem('magazen_theme') || 'dark');

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('magazen_theme', theme);
  }, [theme]);

  return (
    <button
      data-testid="theme-toggle"
      onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
      className="p-2 hover:bg-white/10 rounded-lg text-gray-400 hover:text-white transition-colors"
      title={theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
    >
      {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
    </button>
  );
};

// App Router
const AppRouter = () => {
  const location = useLocation();
  
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
        <Route path="/business" element={<ProtectedRoute><BusinessHubPage /></ProtectedRoute>} />
        <Route path="/genealogy" element={<ProtectedRoute><GenealogyPage /></ProtectedRoute>} />
        <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
        <Route path="/transactions" element={<ProtectedRoute><TransactionHistoryPage /></ProtectedRoute>} />
        <Route path="/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        <Route path="/boutique" element={<BoutiquePage />} />
        <Route path="/education" element={<ProtectedRoute><EducationPage /></ProtectedRoute>} />
        <Route path="/admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
        <Route path="/admin/whatsapp" element={<AdminRoute><WhatsAppAdminPage /></AdminRoute>} />
        <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccessPage /></ProtectedRoute>} />
        <Route path="/payment/cancel" element={<ProtectedRoute><PaymentCancelPage /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <FloatingWhatsAppButton />
    </>
  );
};

// Main App
function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" richColors theme="dark" />
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
